import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { fetchUserWorkStats } from "../../../api/user";
import { ResourceLoading } from "../../../components/utils/ResourceLoading";
import { PMNotice } from "../../../components/utils/notifications";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import {
  ClockIcon,
  CalendarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";

export const WorkStats = () => {
  const { user_id } = useParams();
  const currentUser = useSelector((state) => state.session.currentUser);
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [stats, setStats] = useState(null);

  // Use user_id from params or fall back to current user
  const targetUserId = user_id || currentUser?.id;

  useEffect(() => {
    if (!targetUserId) {
      setIsLoading(false);
      return;
    }

    const fetchStats = async () => {
      setIsLoading(true);
      setError(false);

      try {
        const response = await fetchUserWorkStats(targetUserId);

        if (response?.isAxiosError) {
          throw response;
        }

        if (response?.status === 203) {
          setStats(null);
          return;
        }

        if (response?.status !== 200) {
          throw new Error(`Server error: ${response.status}`);
        }

        setStats(response.data);
      } catch (err) {
        console.error("Error fetching work stats:", err);
        toast.error(t("Something went wrong while loading work stats"));
        setError(true);
      } finally {
        setIsLoading(false);
      }
    };

    fetchStats();
  }, [targetUserId]);

  const StatCard = ({ title, value, icon: Icon, color = "blue" }) => (
    <div
      className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center">
        <div className={`flex-shrink-0 p-3 rounded-lg bg-${color}-100`}>
          <Icon className={`h-6 w-6 text-${color}-600`} />
        </div>
        <div className="ml-4">
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
      </div>
    </div>
  );

  const ProgressBar = ({ value, max, label, color = "blue" }) => (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-2">
        <span className="text-sm font-medium text-gray-700">{label}</span>
        <span className="text-sm text-gray-500">
          {value}h / {max}h
        </span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2">
        <div
          className={`bg-${color}-600 h-2 rounded-full transition-all duration-300`}
          style={{ width: `${Math.min((value / max) * 100, 100)}%` }}
        ></div>
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <DashboardHeader title="Work Statistics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col items-center justify-center gap-3 h-64" role="status" aria-live="polite">
            <ResourceLoading size="base" />
            <p className="text-sm text-slate-500">{t("work_stats.loading")}</p>
          </div>
        </div>
      </DashboardHeader>
    );
  }

  if (error) {
    return (
      <DashboardHeader title="Work Statistics">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <PMNotice
            title={t("Could not load work statistics")}
            severity="error"
          />
        </div>
      </DashboardHeader>
    );
  }

  if (!stats) {
    return (
      <DashboardHeader
        title={t("Work Statistics")}
        subtitle={t("Overview of your work hours and performance")}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <UserIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">
              {t("No data available")}
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              {t("No work statistics found for this user.")}
            </p>
          </div>
        </div>
      </DashboardHeader>
    );
  }

  const weeklyProgress = Math.min(
    (stats.total_worked_week / stats.expected_week) * 100,
    100
  );
  const isOverTime = stats.overtime_week > 0;
  const isUnderTime = stats.remaining_week > 0;

  return (
    <DashboardHeader
      title={t("Work Statistics")}
      subtitle={t("Overview of your work hours and performance")}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title={t("Expected This Week")}
            value={`${stats.expected_week}h`}
            icon={ClockIcon}
            color="blue"
          />
          <StatCard
            title={t("Worked This Week")}
            value={`${stats.total_worked_week}h`}
            icon={CalendarIcon}
            color="green"
          />
          <StatCard
            title={t("Worked This Month")}
            value={`${stats.total_worked_month}h`}
            icon={CalendarIcon}
            color="purple"
          />
          <StatCard
            title={t("Earned Time Off")}
            value={`${stats.total_compensatory_time_off}h`}
            icon={CheckCircleIcon}
            color="emerald"
          />
        </div>

        {/* Weekly Progress */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t("Weekly Progress")}
          </h2>
          <ProgressBar
            value={stats.total_worked_week}
            max={stats.expected_week}
            label={t("Hours worked this week")}
            color={
              weeklyProgress >= 100
                ? "green"
                : weeklyProgress >= 80
                ? "yellow"
                : "blue"
            }
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <ExclamationTriangleIcon className="h-5 w-5 text-orange-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">
                  {t("Remaining")}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.remaining_week}h
              </p>
            </div>

            <div className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">
                  {t("Overtime")}
                </span>
              </div>
              <p className="text-2xl font-bold text-gray-900 mt-1">
                {stats.overtime_week}h
              </p>
            </div>
          </div>
        </div>

        {/* Status Indicators */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            className={`rounded-lg p-4 ${
              isOverTime
                ? "bg-green-50 border border-green-200"
                : "bg-gray-50 border border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <CheckCircleIcon
                className={`h-5 w-5 ${
                  isOverTime ? "text-green-500" : "text-gray-400"
                } mr-2`}
              />
              <span className="text-sm font-medium text-gray-700">
                {t("Weekly Goal")}
              </span>
            </div>
            <p
              className={`text-lg font-semibold mt-1 ${
                isOverTime ? "text-green-700" : "text-gray-500"
              }`}
            >
              {isOverTime ? t("Completed") : t("In Progress")}
            </p>
          </div>

          <div
            className={`rounded-lg p-4 ${
              isUnderTime
                ? "bg-yellow-50 border border-yellow-200"
                : "bg-gray-50 border border-gray-200"
            }`}
          >
            <div className="flex items-center">
              <ExclamationTriangleIcon
                className={`h-5 w-5 ${
                  isUnderTime ? "text-yellow-500" : "text-gray-400"
                } mr-2`}
              />
              <span className="text-sm font-medium text-gray-700">
                {t("Status")}
              </span>
            </div>
            <p
              className={`text-lg font-semibold mt-1 ${
                isUnderTime ? "text-yellow-700" : "text-gray-500"
              }`}
            >
              {isUnderTime ? t("Under Target") : t("On Track")}
            </p>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center">
              <ClockIcon className="h-5 w-5 text-blue-500 mr-2" />
              <span className="text-sm font-medium text-gray-700">
                {t("Efficiency")}
              </span>
            </div>
            <p className="text-lg font-semibold text-blue-700 mt-1">
              {Math.round(
                (stats.total_worked_week / stats.expected_week) * 100
              )}
              %
            </p>
          </div>
        </div>
      </div>
    </DashboardHeader>
  );
};
